import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import { useEventTypeOptions } from '../../common/hooks/useEventTypeOptions'
import { useTrainerOptions } from '../../common/hooks/useTrainerOptions'
import { useSocialLinksOptions } from '../../common/hooks/useSocialLinksOptions'
import {
  getInitialTimeStart,
  getInitialTimeEnd,
  defaultFrequencyOption,
} from '../../common/helpers'
import { EMAIL_REGEX } from '../../../../helpers/constants'
import { mapAppointmentDtoToFormData } from '../mappers/index'

const getInitialValues = (timezone) => ({
  // Event details
  type: null,
  name: '',
  organizer: null,
  mainClient: null,
  description: '',
  image: null,
  files: [],
  // Date/Time
  place: '',
  timeStart: getInitialTimeStart(timezone),
  timeEnd: getInitialTimeEnd(timezone),
  frequency: defaultFrequencyOption,
  // Advanced - Communication
  automatedRemindersToggle: false,
  remindRsvp: false,
  remindRsvpValues: [''],
  remindAll: false,
  remindAllValues: [''],
  askForPostEventReview: false,
  positiveReview: false,
  socialLinkService: '',
  replyTo: '',
})

const makeValidationSchema = (t) =>
  Yup.object().shape({
    type: Yup.object()
      .nullable()
      .required(t('eventManagement:appointmentFormPage.validation.typeRequired')),
    name: Yup.string().required(t('eventManagement:appointmentFormPage.validation.nameRequired')),
    organizer: Yup.object()
      .nullable()
      .required(t('eventManagement:appointmentFormPage.validation.facilitatorRequired')),
    description: Yup.string().required(
      t('eventManagement:appointmentFormPage.validation.descriptionRequired'),
    ),
    image: Yup.object()
      .nullable()
      .required(t('eventManagement:appointmentFormPage.validation.photoRequired')),
    location: Yup.string().required(
      t('eventManagement:appointmentFormPage.validation.locationRequired'),
    ),
    timeStart: Yup.object().required(
      t('eventManagement:appointmentFormPage.validation.dateTimeRequired'),
    ),
    replyTo: Yup.string().when('positiveReview', {
      is: true,
      then: (schema) =>
        schema
          .required(t('eventManagement:appointmentFormPage.validation.replyToRequired'))
          .matches(EMAIL_REGEX, t('eventManagement:appointmentFormPage.validation.invalidEmail')),
    }),
  })

export const useAppointmentFormState = ({ userData, eventData, t }) => {
  const selectedBuilding = useSelector(selectActiveBuilding)
  const selectedBuildingId = selectedBuilding ? selectedBuilding.id : null
  const selectedBuildingTimezone = selectedBuilding?.timezone

  const { eventTypeOptions, isLoading: isEventTypeOptionsLoading } = useEventTypeOptions(userData)
  const { trainerOptions, isLoading: isTrainerOptionsLoading } = useTrainerOptions(userData)
  const { isLoading: isSocialLinksLoading } = useSocialLinksOptions(selectedBuildingId)

  const { availableOptions: socialLinkOptions } = useSocialLinksOptions(selectedBuildingId, true)

  const initialValues = useMemo(() => {
    return eventData
      ? mapAppointmentDtoToFormData({
          eventData,
          trainerOptions,
          eventTypeOptions,
        })
      : getInitialValues(selectedBuildingTimezone)
  }, [eventData, eventTypeOptions, trainerOptions])

  const validationSchema = useMemo(() => makeValidationSchema(t), [t])

  return {
    initialValues,
    validationSchema,
    eventTypeOptions,
    trainerOptions,
    availableOptions: socialLinkOptions,
    areOptionsLoading: isEventTypeOptionsLoading || isTrainerOptionsLoading || isSocialLinksLoading,
  }
}
